import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listPlatformActivity,
  platformActionMeta,
  platformTimeAgo,
  PLATFORM_ACTION_META,
  type PlatformActivityEntry,
} from '@/lib/platformActivity';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Journal d'activité des travailleurs plateforme.
// « Qui a fait quoi » dans le back-office, avec horodatage.
// ─────────────────────────────────────────────────────────────

const ACTION_KEYS = Object.keys(PLATFORM_ACTION_META);

export default function SuperAdminActivityPage() {
  const [entries, setEntries] = useState<PlatformActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await listPlatformActivity(300));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le journal.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const last7 = entries.filter((e) => e.createdAt && new Date(e.createdAt).getTime() >= sevenDaysAgo).length;
    const actors = new Set(entries.map((e) => e.actorId)).size;
    return { total: entries.length, last7, actors };
  }, [entries]);

  const filtered = entries.filter((e) => {
    if (actionFilter !== 'all' && e.action !== actionFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!e.userName.toLowerCase().includes(q) && !e.description.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-history-line text-primary-600"></i>
            Journal d&apos;activité
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Tout ce que font vos travailleurs plateforme dans le back-office — qui, quoi, et quand.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50 self-start"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Actualiser
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6 max-w-xl">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-foreground-950">{stats.total}</div>
          <div className="text-xs text-foreground-500 mt-0.5">Actions tracées</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-accent-600">{stats.last7}</div>
          <div className="text-xs text-foreground-500 mt-0.5">7 derniers jours</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="text-2xl font-bold font-heading text-primary-600">{stats.actors}</div>
          <div className="text-xs text-foreground-500 mt-0.5">Auteurs</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Rechercher un auteur ou une action..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
          />
        </div>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
        >
          <option value="all">Tous les types d&apos;action</option>
          {ACTION_KEYS.map((key) => (
            <option key={key} value={key}>{PLATFORM_ACTION_META[key].label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center py-16 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button onClick={load} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm cursor-pointer">Réessayer</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-history-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucune activité enregistrée pour le moment.</p>
          <p className="text-xs text-foreground-400 mt-1">Les actions des travailleurs (ajout, accès, réglages PWA…) apparaîtront ici.</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="divide-y divide-background-200/70">
            {filtered.map((e) => {
              const meta = platformActionMeta(e.action);
              return (
                <div key={e.id} className="flex items-start gap-3 px-4 md:px-5 py-4 hover:bg-background-50/60">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${meta.className}`}>
                    <i className={`${meta.icon} text-base`}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-foreground-950">{e.userName}</span>
                      {e.actorRole && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium">
                          <i className="ri-shield-user-line"></i>{e.actorRole}
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.className}`}>
                        <i className={meta.icon}></i>{meta.label}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-700 mt-1 break-words">{e.description}</p>
                    <p className="text-xs text-foreground-400 mt-1 flex items-center gap-1.5">
                      <i className="ri-time-line"></i>
                      {platformTimeAgo(e.createdAt)}
                      {e.createdAt && (
                        <span className="text-foreground-300">
                          · {new Date(e.createdAt).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-4 md:px-5 py-3 border-t border-background-200/70 text-xs text-foreground-500">
            {filtered.length} entrée{filtered.length > 1 ? 's' : ''} affichée{filtered.length > 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
}